import { useControlledState } from "@react-stately/utils";
import { CurrencyDollar } from "@untitledui/icons";
import { HintText } from "@/components/base/input/hint-text";
import type { InputBaseProps } from "@/components/base/input/input";
import { InputBase, TextField } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";

export const formatCurrency = (value: string): string => {
  // Remove all non-numeric characters except decimal point
  const cleaned = value.replace(/[^\d.]/g, "");

  // Handle multiple decimal points by keeping only the first one
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    const integer = parts[0];
    const decimal = parts.slice(1).join("");
    return `${integer}.${decimal}`;
  }

  const [integer, decimal] = parts;

  // If no integer part, return empty or just decimal
  if (!integer) {
    return decimal ? `0.${decimal}` : "";
  }

  // Remove leading zeros from integer part
  const cleanInteger = integer.replace(/^0+/, "") || "0";

  // Format integer part with commas
  const formattedInteger = cleanInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Handle decimal part
  if (decimal !== undefined) {
    // Limit decimal to 2 places
    const limitedDecimal = decimal.slice(0, 2);
    return `${formattedInteger}.${limitedDecimal}`;
  }

  return formattedInteger;
};

export const parseCurrencyValue = (formattedValue: string): string => {
  // Remove commas and return numeric value
  return formattedValue.replace(/,/g, "");
};

export interface CurrencyInputProps extends InputBaseProps {
  /** Currency symbol to display */
  currencySymbol?: string;
  /** Whether to show the currency symbol */
  showCurrencySymbol?: boolean;
}

export const CurrencyInput = ({
  onChange,
  value,
  defaultValue,
  className,
  maxLength = 19,
  label,
  hint,
  currencySymbol = "$",
  showCurrencySymbol = true,
  "aria-label": ariaLabelProp,
  id,
  name,
  ...props
}: CurrencyInputProps) => {
  const [displayValue, setDisplayValue] = useControlledState(
    value ? formatCurrency(value) : "",
    defaultValue ? formatCurrency(defaultValue) : "",
    (newDisplayValue) => {
      const numericValue = parseCurrencyValue(newDisplayValue || "");
      onChange?.(numericValue);
    },
  );

  const handleChange = (newValue: string) => {
    // Allow empty value
    if (newValue === "") {
      setDisplayValue("");
      return;
    }

    // Format the input value
    const formatted = formatCurrency(newValue);
    setDisplayValue(formatted);
  };

  return (
    <TextField
      aria-label={ariaLabelProp || label || props?.placeholder}
      {...props}
      className={className}
      maxLength={maxLength}
      value={displayValue}
      onChange={handleChange}
    >
      {({ isDisabled, isInvalid, isRequired }) => (
        <>
          {label && <Label isRequired={isRequired}>{label}</Label>}

          <InputBase
            {...props}
            id={id}
            name={name}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            icon={showCurrencySymbol ? CurrencyDollar : undefined}
            inputClassName={showCurrencySymbol ? "pl-13" : undefined}
            iconClassName={
              showCurrencySymbol ? "left-2.5 h-6 w-8.5" : undefined
            }
          />

          {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
        </>
      )}
    </TextField>
  );
};

CurrencyInput.displayName = "CurrencyInput";
